/**
 * Options Chain API — live chain (juice/greeks/OI) + ATR strike tiers.
 * Data flows from whichever gateway session is up (paper-preferred);
 * 503 means no reachable gateway — render the message, substitute nothing.
 */
import { apiGet } from './client'

export interface ChainExpirations {
  symbol: string
  /** '/GC' for futures — the string every surface shows. */
  display_symbol?: string
  underlying_conid: number
  trading_class: string
  multiplier: string
  expirations: string[] // YYYYMMDD sorted
  strikes: number[]
  und_price: number | null
  /** 'FUT' for futures underlyings. */
  sec_type?: string
  /** Active contract month (YYYYMMDD) for futures underlyings. */
  contract_month?: string | null
  /** The STANDARD (monthly) expiries, derived server-side from the option
   *  trading classes. Futures only — "monthly" is not inferable from the date
   *  on a futures product (gold's monthlies are the 24th-26th, and the only
   *  /GC date the third-Friday rule matched was a WEEKLY). Absent for
   *  equities and index, where the date rule is correct. */
  monthly_expirations?: string[] | null
  /** Selectable contract months (YYYYMMDD, nearest first) for futures.
   *  Filtered to months that actually list options — a futures root has far
   *  more contract months than option series. */
  contract_months?: string[]
}

export interface ChainQuoteRow {
  conid: number
  strike: number
  right: 'C' | 'P'
  bid: number | null
  ask: number | null
  last: number | null
  mark: number | null
  model_price: number | null
  volume: number | null
  open_interest: number | null
  delta: number | null
  gamma: number | null
  theta: number | null
  vega: number | null
  iv: number | null
  intrinsic: number | null
  extrinsic: number | null
}

export interface ChainResponse {
  symbol: string
  /** '/GC' for futures — the string every surface shows. */
  display_symbol?: string
  expiry: string
  und_price: number | null
  multiplier: string | null
  count_requested: number
  /** Per-right |delta| bands in effect, e.g. { P: [0.10, 0.30], C: [0.70, 0.90] }. */
  band?: Record<string, [number, number]> | null
  /** True when the band covered more strikes than the line budget — evenly subsampled. */
  thinned?: boolean
  /**
   * How the gateway's streaming-line allowance was divided. Every open chain
   * view gets `total / (sharing_with + 1)` lines; `sharing_with > 0` means
   * another chain is open and this ladder is deliberately shorter.
   */
  line_budget?: {
    share: number
    total: number
    sharing_with: number
  }
  rows: ChainQuoteRow[]
}

export interface ATRResponse {
  symbol: string
  atr: number
  period: number
  bar_size: string
  bars_used: number
  last_bar_date: string
  underlying_price: number | null
  suggested_call_strike: number | null
  suggested_put_strike: number | null
}

export const getChainExpirations = (symbol: string, month?: string) =>
  apiGet<ChainExpirations>('/options/expiries', { params: { symbol, ...(month ? { month } : {}) } })

export const getChainQuotes = (params: {
  symbol: string
  expiry: string
  count?: number
  rights?: string
  month?: string
  delta_max?: number
  delta_min?: number
  /** Per-right |delta| bands (override delta_min/max for that right). */
  put_delta_min?: number
  put_delta_max?: number
  call_delta_min?: number
  call_delta_max?: number
  /** CSV of strikes that must survive the market-data line cap (held positions). */
  pin_strikes?: string
  /** Stable id of this chain panel — the server's line-budget sharing counts
   *  panels, not the expiries one panel has recently flipped through. */
  view_id?: string
}) => apiGet<ChainResponse>('/options/chain', { params, timeout: 60_000 })

export const getATR = (symbol: string, period = 10, barSize: '1 day' | '1 week' = '1 week') =>
  apiGet<ATRResponse>('/options/atr', { params: { symbol, period, bar_size: barSize } })

/** Known futures roots (registry ∪ IB-discovered) for display fallbacks. */
export const getFuturesRoots = () => apiGet<{ roots: string[] }>('/instruments/futures-roots')

// ─── EOD analytics (as_of-labelled — never substituted for live) ─────────────

export interface IVMetricsResponse {
  symbol: string
  iv: number | null
  iv_rank: number | null
  iv_percentile: number | null
  iv_low: number | null
  iv_high: number | null
  as_of: string | null
  observations: number
  dte_target: number
  lookback_days: number
  /** Why rank/percentile are null (thin history, no coverage). Show it. */
  note: string | null
}

export interface ExpectedMoveRow {
  expiry: string
  days_to_expiry: number
  move: number | null
  move_pct: number | null
  lower: number | null
  upper: number | null
  /** Full 1 SD — larger than `move` by ~1.47x. Not the same quantity. */
  sigma_move: number | null
  method: 'straddle' | 'iv' | null
  spot: number | null
  atm_iv: number | null
  atm_strike: number | null
  as_of: string | null
  note: string | null
}

/** IV Rank + IV Percentile (EOD, 30-DTE constant maturity). */
export const getIVMetrics = (symbol: string) =>
  apiGet<IVMetricsResponse>('/options/iv-metrics', { params: { symbol } })

/** Expected move per expiration from the nightly chain snapshot (EOD). */
export const getExpectedMoves = (symbol: string, maxExpiries = 24) =>
  apiGet<{ symbol: string; expected_moves: ExpectedMoveRow[]; count: number }>(
    '/options/expected-move', { params: { symbol, max_expiries: maxExpiries } })
